import { T } from "@i18n/T";
import { cn } from "@shadcn/lib/utils";
import { Button } from "@shadcn/ui/button";
import { Checkbox } from "@shadcn/ui/checkbox";
import { Field, FieldContent, FieldDescription, FieldError, FieldLabel } from "@shadcn/ui/field";
import { Input } from "@shadcn/ui/input";
import { InputGroup, InputGroupAddon, InputGroupButton, InputGroupInput } from "@shadcn/ui/input-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@shadcn/ui/select";
import { Textarea } from "@shadcn/ui/textarea";
import { createFormHook, createFormHookContexts } from "@tanstack/react-form";
import { Eye, EyeClosed } from "lucide-react";
import { ComponentProps, PropsWithChildren, ReactNode, useState } from "react";

export const { fieldContext, formContext, useFieldContext, useFormContext } = createFormHookContexts();

type InputFieldProps = {
	label: string;
	description?: string;
	fieldProps?: ComponentProps<typeof Field>;
} & Omit<ComponentProps<typeof Input>, "name" | "value" | "onChange">;

export const InputField = (props: InputFieldProps) => {
	const { label, description, fieldProps, ...rest } = props;
	const field = useFieldContext<string>();

	const isInvalid = !field.state.meta.isValid;
	return (
		<Field data-invalid={isInvalid} {...fieldProps}>
			<FieldLabel htmlFor={field.name}>
				<T>{label}</T>
			</FieldLabel>
			{description ? (
				<FieldDescription>
					<T>{description}</T>
				</FieldDescription>
			) : null}
			<Input id={field.name} name={field.name} value={field.state.value} onChange={(e) => field.handleChange(e.target.value)} {...rest} />
			{isInvalid && <FieldError errors={field.state.meta.errors} />}
		</Field>
	);
};

type TextareaFieldProps = {
	label: string;
	description?: string;
	fieldProps?: ComponentProps<typeof Field>;
} & Omit<ComponentProps<typeof Textarea>, "name" | "value" | "onChange">;

export const TextareaField = (props: TextareaFieldProps) => {
	const { label, description, fieldProps, ...rest } = props;
	const field = useFieldContext<string>();

	const isInvalid = !field.state.meta.isValid;
	return (
		<Field data-invalid={isInvalid} {...fieldProps}>
			<FieldLabel htmlFor={field.name}>
				<T>{label}</T>
			</FieldLabel>
			{description ? (
				<FieldDescription>
					<T>{description}</T>
				</FieldDescription>
			) : null}
			<Textarea name={field.name} value={field.state.value} onChange={(e) => field.handleChange(e.target.value)} {...rest} />
			{isInvalid && <FieldError errors={field.state.meta.errors} />}
		</Field>
	);
};

type SelectOption = { label: ReactNode; value: string; disabled?: boolean };

type SelectFieldProps = {
	label: string;
	description?: string;
	placeholder?: string;
	options: SelectOption[];
	fieldProps?: ComponentProps<typeof Field>;
	disabled?: boolean;
};

export const SelectField = (props: SelectFieldProps) => {
	const { label, description, placeholder, options, fieldProps, disabled } = props;
	const field = useFieldContext<string>();

	const isInvalid = !field.state.meta.isValid;
	return (
		<Field data-invalid={isInvalid} {...fieldProps}>
			<FieldLabel htmlFor={field.name}>
				<T>{label}</T>
			</FieldLabel>
			{description ? (
				<FieldDescription>
					<T>{description}</T>
				</FieldDescription>
			) : null}
			<Select value={field.state.value} onValueChange={field.handleChange} disabled={disabled}>
				<SelectTrigger id={field.name} disabled={disabled}>
					<SelectValue placeholder={placeholder} />
				</SelectTrigger>
				<SelectContent>
					{options.map(({ label, ...option }) => (
						<SelectItem key={option.value} {...option}>
							{label}
						</SelectItem>
					))}
				</SelectContent>
			</Select>
			{isInvalid && <FieldError errors={field.state.meta.errors} />}
		</Field>
	);
};

type CheckboxFieldProps = {
	label: string;
	hideLabel?: true;
	description?: string;
	fieldProps?: ComponentProps<typeof Field>;
} & Omit<ComponentProps<typeof Checkbox>, "name" | "checked" | "onCheckedChange" | "orientation">;

export const CheckboxField = (props: CheckboxFieldProps) => {
	const { label, description, className, fieldProps, hideLabel, ...rest } = props;
	const field = useFieldContext<boolean>();

	const isInvalid = !field.state.meta.isValid;

	const onLabelClick = (e: React.MouseEvent<HTMLElement>) => {
		const selection = window.getSelection();
		if (selection && selection.toString().length > 0) {
			return;
		}
		if (e.target instanceof HTMLAnchorElement) return;
		field.handleChange(!field.state.value);
	};

	return (
		<Field data-invalid={isInvalid} orientation="horizontal" {...fieldProps}>
			<Checkbox
				name={field.name}
				checked={field.state.value}
				onCheckedChange={(checked) => field.handleChange(checked === "indeterminate" ? false : checked)}
				{...rest}
			/>
			{description ? (
				<FieldContent
					onClick={onLabelClick}
					className={cn({
						"-mt-1": hideLabel,
					})}
				>
					{!hideLabel && (
						<FieldLabel htmlFor={field.name}>
							<span>
								<T>{label}</T>
							</span>
						</FieldLabel>
					)}
					<FieldDescription>
						<T>{description}</T>
					</FieldDescription>
				</FieldContent>
			) : (
				<FieldLabel htmlFor={field.name} onClick={onLabelClick}>
					<span>
						<T>{label}</T>
					</span>
				</FieldLabel>
			)}
			{isInvalid && <FieldError errors={field.state.meta.errors} />}
		</Field>
	);
};

type SubmitButtonProps = Omit<React.ComponentProps<typeof Button>, "type">;

export const SubmitButton = (props: PropsWithChildren<SubmitButtonProps>) => {
	const { children, ...buttonProps } = props;
	const form = useFormContext();
	return (
		<form.Subscribe>
			{(state) => (
				<Button {...buttonProps} type="submit" disabled={state.isSubmitting || buttonProps.disabled}>
					{children}
				</Button>
			)}
		</form.Subscribe>
	);
};

type PasswordFieldProps = {
	label: string;
	description?: string;
	fieldProps?: ComponentProps<typeof Field>;
} & Omit<ComponentProps<typeof Input>, "type" | "name" | "value" | "onChange">;

export const PasswordField = (props: PasswordFieldProps) => {
	const { label, description, fieldProps, ...rest } = props;
	const field = useFieldContext<string>();
	const [showPassword, setShowPassword] = useState(false);

	const isInvalid = !field.state.meta.isValid;
	return (
		<Field data-invalid={isInvalid} {...fieldProps}>
			<FieldLabel htmlFor={field.name}>
				<T>{label}</T>
			</FieldLabel>
			<InputGroup>
				<InputGroupInput
					type={showPassword ? "text" : "password"}
					name={field.name}
					value={field.state.value}
					onChange={(e) => field.handleChange(e.target.value)}
					{...rest}
				/>
				<InputGroupAddon align="inline-end">
					<InputGroupButton
						aria-label="Copy"
						title="Copy"
						size="icon-xs"
						onClick={() => {
							setShowPassword(!showPassword);
						}}
					>
						{showPassword ? <Eye /> : <EyeClosed />}
					</InputGroupButton>
				</InputGroupAddon>
			</InputGroup>
			{description && !isInvalid ? (
				<FieldDescription>
					<T>{description}</T>
				</FieldDescription>
			) : null}
			{isInvalid && <FieldError errors={field.state.meta.errors} />}
		</Field>
	);
};

const { useAppForm, withForm, withFieldGroup } = createFormHook({
	fieldContext,
	formContext,
	fieldComponents: {
		Input: InputField,
		Textarea: TextareaField,
		Select: SelectField,
		Checkbox: CheckboxField,
		Password: PasswordField,
	},
	formComponents: {
		SubmitButton,
	},
});

export { useAppForm, withFieldGroup, withForm };
export type { CheckboxFieldProps, SelectFieldProps, SubmitButtonProps, TextareaFieldProps, InputFieldProps as TextFieldProps };
